import React from 'react';
import { Card } from '@/components/ui';

interface UsageMetricProps {
  label: string;
  current: number;
  max: number;
  unit?: string;
  color: 'primary' | 'emerald' | 'blue' | 'orange';
}

function UsageMetric({ label, current, max, unit = '', color }: UsageMetricProps) {
  const percentage = Math.min((current / max) * 100, 100);

  const colorMap = {
    primary: 'bg-os-primary',
    emerald: 'bg-emerald-500',
    blue: 'bg-blue-500',
    orange: 'bg-orange-500'
  };

  const bgMap = {
    primary: 'bg-os-primary/10',
    emerald: 'bg-emerald-50',
    blue: 'bg-blue-50',
    orange: 'bg-orange-50'
  };

  return (
    <div className="space-y-2">
      <div className="flex justify-between items-end">
        <div>
          <p className="text-[10px] font-bold text-slate-400  tracking-wider">{label}</p>
          <p className="text-lg font-bold text-slate-900">
            {current} <span className="text-xs text-slate-400 font-normal">/ {max} {unit}</span>
          </p>
        </div>
        <span className="text-xs font-bold text-slate-500">{Math.round(percentage)}%</span>
      </div>
      <div className={`h-2 w-full ${bgMap[color]} rounded-full overflow-hidden`}>
        <div
          className={`h-full ${colorMap[color]} transition-all duration-1000`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

interface CompanyUsageCardProps {
  usage: {
    projects_count: number;
    users_count: number;
    assets_count: number;
    max_projects: number;
    max_users: number;
    max_assets: number;
  };
}

export function CompanyUsageCard({ usage }: CompanyUsageCardProps) {
  return (
    <Card className="p-6">
      <h3 className="font-bold text-slate-900 mb-6 flex items-center gap-2">
        <i className="ri-bar-chart-2-line text-os-primary" />
        Sử dụng Tài nguyên
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <UsageMetric
          label="Dự án"
          current={usage.projects_count}
          max={usage.max_projects}
          color="primary"
          unit="Dự án"
        />
        <UsageMetric
          label="Thành viên"
          current={usage.users_count}
          max={usage.max_users}
          color="blue"
          unit="Người"
        />
        <UsageMetric
          label="Tài sản / Cây xanh"
          current={usage.assets_count}
          max={usage.max_assets}
          color="emerald"
          unit="Mục"
        />
      </div>
    </Card>
  );
}
